import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import AdminAccessPage from "./pages/AdminAccessPage";
import AffiliateDashboard from "./pages/affiliate/Dashboard";
import AffiliateLinks from "./pages/affiliate/Links";
import AffiliateMerchants from "./pages/affiliate/Merchants";
import AffiliateEarnings from "./pages/affiliate/Earnings";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminAffiliates from "./pages/admin/Affiliates";
import AdminAffiliateDetail from "./pages/admin/AffiliateDetail";
import AdminMerchants from "./pages/admin/Merchants";
import AdminPayouts from "./pages/admin/Payouts";
import AdminSettings from "./pages/admin/Settings";
import IntegrationDocs from "./pages/IntegrationDocs";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

// Hooks
import usePageMeta from "./hooks/usePageMeta";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (response.ok) {
          const userData = await response.json();
          // Store token for backup
          localStorage.setItem("user", JSON.stringify(userData));
          
          // Redirect based on role
          if (userData.role === "admin") {
            navigate("/admin", { state: { user: userData } });
          } else {
            navigate("/app", { state: { user: userData } });
          }
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // If user was passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setChecking(false);
      return;
    }

    const verify = async () => {
      const isAuth = await checkAuth();
      if (!isAuth) {
        navigate("/login", { replace: true });
      }
      setChecking(false);
    };

    verify();
  }, [checkAuth, navigate, location.state]);

  if (loading || checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  usePageMeta(location.pathname);

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/integration" element={<IntegrationDocs />} />

      {/* Affiliate routes */}
      <Route
        path="/app"
        element={
          <ProtectedRoute allowedRoles={["affiliate", "admin"]}>
            <AffiliateDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/links"
        element={
          <ProtectedRoute allowedRoles={["affiliate", "admin"]}>
            <AffiliateLinks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/merchants"
        element={
          <ProtectedRoute allowedRoles={["affiliate", "admin"]}>
            <AffiliateMerchants />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/earnings"
        element={
          <ProtectedRoute allowedRoles={["affiliate", "admin"]}>
            <AffiliateEarnings />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/affiliates"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminAffiliates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/affiliates/:affiliateId"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminAffiliateDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/merchants"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminMerchants />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payouts"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminPayouts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminSettings />
          </ProtectedRoute>
        }
      />

      {/* Password-only admin access */}
      <Route path="/maaly-admin-access" element={<AdminAccessPage />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
